<template>
	<BaseColumn
		:title="title"
		:subtitle="transUcfirst('tag_group.select_tag_group')"
		:size="1"
		class="tag-groups-column"
	>
		<template
			v-if="isMobileWebApp"
			#title="{title, subtitle}"
		>
			<div
				v-if="title"
				class="settings-column--title"
			>
				<h2>{{ title }}</h2>
				<p
					class="settings-column--subtitle"
				>
					{{ subtitle }}
				</p>
				<p class="setting-column--subtitle info">
					<Icon
						icon-name="c-info"
						size="sm"
					/>
					<span>{{ transUcfirst('tag_group.tag_create_missing_info') }}</span>
				</p>
			</div>
		</template>
		<template #body>
			<div
				class="tag-groups"
			>
				<div
					v-for="(tagGroup) in tagGroups"
					:key="tagGroup.id"
					class="tag-group"
					:class="{'selected': isSelected(tagGroup)}"
					@click="() => onTagGroupClick(tagGroup)"
				>
					<div class="leading">
						<input
							type="radio"
							:checked="isSelected(tagGroup)"
						>
					</div>
					<div class="tag-group--name">
						<span class="label">{{ tagGroup.name }}</span>
						<span
							v-tooltip="{content: visibilityLabels[tagGroup.visibility], classes: 'tooltip'}"
							class="icon"
						>
							<Icon
								v-if="tagGroup.isPublic || tagGroup.isPrivate"
								:icon-name="tagGroup.isPublic ? 'world' : (tagGroup.isPrivate ? 'lock' : '')"
								size="sm"
							/>
						</span>
					</div>
					<ContextMenu
						v-if="canEdit"
						:menu-items="tagGroupMenuItems(tagGroup)"
						:value="tagGroup"
					>
						<template #toggle>
							<Icon
								icon-name="context-menu"
								size="lg"
							/>
						</template>
					</ContextMenu>
				</div>
			</div>
			<div
				v-if="canCreate"
				class="create"
			>
				<button
					class="button button--primary"
					@click="$emit('create-tag-group')"
				>
					<span><Icon icon-name="e-add" /></span>
					<span class="label">{{ trans('tag_group.create') }}</span>
				</button>
			</div>
		</template>
	</BaseColumn>
</template>

<script>
import BaseColumn from '@videotools/js/analysis/components/Settings/BaseColumn';
import { mapActions, mapGetters } from 'vuex';
import ContextMenu from '@components/context-menu/ContextMenu';
import TagGroup, { ALL_VISIBILITIES, VISIBILITY_CONTEXT } from '@videotools/js/api/entities/TagGroup';

export default {
	name:       'TagGroupsList',
	components: { ContextMenu, BaseColumn },
	props:      {
		selected: {
			type:    TagGroup,
			default: null,
		},
		canCreate: {
			type:    Boolean,
			default: true,
		},
		canEdit: {
			type:    Boolean,
			default: true,
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
			sportName:      'event/sportName',
			tagGroups:      'tagGroups/tagGroups',
			contextName:    'user/contextName',
		}),
		title() {
			return `${this.transChoiceUcFirst('tag_group.tag_group', 2)} (${this.transSportName(this.sportName)})`;
		},
		visibilityLabels() {
			return ALL_VISIBILITIES.reduce(
				(acc, visibility) => {
					const label = visibility === VISIBILITY_CONTEXT
						? this.transUcfirst(`tag_group.visibilities.${visibility}.${this.contextName}`)
						: this.transUcfirst(`tag_group.visibilities.${visibility}`);
					return { [visibility]: label, ...acc };
				},
				{})
			;
		},
	},
	methods: {
		...mapActions({
			deleteTagGroup: 'tagGroups/delete',
		}),
		onTagGroupClick(tagGroup) {
			this.$emit('change', tagGroup);
		},
		onEdit(tagGroup) {
			this.$emit('edit-tag-group', tagGroup);
		},
		onDuplicate(tagGroup) {
			this.$emit('duplicate-tag-group', tagGroup);
		},
		async onDelete(tagGroup) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('tag_group.tag_group', 1),
					}),
					body:   this.$I18n.trans('tag_group.delete_confirmation'),
					target: tagGroup.name,
				});
				await this.deleteTagGroup(tagGroup.id);
			} catch (e) {
				// do nothing, most likely confirmation canceled
			}
		},
		isSelected(tagGroup) {
			return this.selected && tagGroup.id === this.selected.id;
		},
		tagGroupMenuItems(tagGroup) {
			const items = [
				{
					id:      'edit',
					label:   this.transUcfirst('commons.edit'),
					icon:    'pen',
					handler: this.onEdit,
				},
				...!tagGroup.isSystemDefault
					? [{
						id:      'duplicate',
						label:   this.transUcfirst('commons.duplicate'),
						icon:    'copy',
						handler: this.onDuplicate,
					}]
					: [],
			];

			if (!tagGroup.isPublic) {
				items.push({
					id:      'delete',
					label:   this.transUcfirst('commons.delete'),
					icon:    'bin',
					handler: this.onDelete,
					class:   'danger',
				});
			}

			return items;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.tag-groups-column {
	overflow: hidden;
}

.tag-groups {
	padding: 8px 0;
	overflow: auto;
}

.tag-group {
	padding: 12px 12px 12px 16px;
	background: var(--color-grey-900);
	border: 1px solid var(--color-grey-900);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:not(:last-child) {
		margin-bottom: 8px;
	}

	> *:not(:last-child) {
		margin-right: 8px;
	}

	.context-menu {
		flex-grow: 0;
		text-align: right;
	}

	&.selected {
		border: 1px solid var(--color-hype-yellow);
		border-radius: 2px;
	}

	.tag-group--name {
		margin-right: 16px;
		flex-grow: 1;
		display: flex;
		align-items: center;
		min-width: 0;

		.label {
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
		}
		.icon {
			margin-left: 16px;
			color: var(--color-grey-300);
		}
	}

	:not(.is-mobile) &:hover {
		background-color: var(--color-grey-800);

		&:not(.selected) {
			border-color: var(--color-grey-800);
		}
	}
}

.create {
	margin-top: 16px;
	margin-bottom: 24px;

	.button--primary {
		display: flex;
		align-items: center;
		width: 100%;

		span {
			flex-grow: 1;
		}

		span:first-child {
			margin-right: 8px;
			display: flex;
			justify-content: flex-end;
		}

		.label {
			text-align: start;
		}
	}
}

.leading {
	display: flex;
}

input[type=radio] {
	color: var(--color-grey-300);
	-webkit-appearance: none;
	appearance: none;
	width: 16px;
	height: 16px;
	border: 1px solid currentColor;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: "";
		width: 8px;
		height: 8px;
		border-radius: 50%;
		transform: scale(0);
		transition: 120ms transform ease-in-out;
		box-shadow: inset 1em 1em var(--form-control-color);
		background-color: var(--color-hype-yellow);
		display: flex;
	}
}

input[type=radio]:checked {
	color: var(--color-hype-yellow);
	&::before {
		transform: scale(1);
	}
}

::v-deep .body {
	max-height: 84%;
}

.setting-column--subtitle.info {
	display: flex;

	> *:first-child {
		margin-right: 8px;
	}
}
</style>
